import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Tooltip,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { FlagIcon } from "src/components/iconify/flagIcon";
import CustomLineChart from "src/components/inline-chart/inline-chart-activations-scatter";
import { useUpdateKoBiddingManualMaxCpcMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import countriesJson from "src/utils/countries.json";

export default function ModalActivationsManual({
    open,
    row,
    onClose,
    setLocalPatches,
}: {
    open: boolean;
    row: any;
    onClose: () => void;
    setLocalPatches: any;
}) {
    const workspace = useCurrentWorkspace();
    const [updateMaxCpc] = useUpdateKoBiddingManualMaxCpcMutation();

    const rowActivation =
        row?.activations_details?.find(
            (d: any) => d.mode === "KO Bidding Manual"
        ) || null;

    const [maxCpc, setMaxCpc] = useState<number | "">("");

    useEffect(() => {
        if (rowActivation?.max_cpc !== undefined) {
            setMaxCpc(rowActivation.max_cpc);
        }
    }, [rowActivation]);

    const handleApplyChanges = async () => {
        try {
            if (maxCpc === "" || isNaN(Number(maxCpc))) {
                enqueueSnackbar("Please set a valid Max CPC.", { variant: "warning" });
                return;
            }

            const updated = await updateMaxCpc({
                id: rowActivation?.id,
                workspaceId: workspace?.id,
                max_cpc: Number(maxCpc),
            }).unwrap();

            setLocalPatches((prev: any) => {
                const existing = prev[row.id] ?? row;
                return {
                    ...prev,
                    [row.id]: {
                        ...existing,
                        activations_details: existing.activations_details.map((d: any) =>
                            d.id === rowActivation.id
                                ? { ...d, max_cpc: updated.new_max_cpc }
                                : d
                        ),
                    },
                };
            });

            enqueueSnackbar("Max CPC updated successfully", {
                variant: "success",
            });
            onClose();
        } catch {
            enqueueSnackbar("Failed to update Max CPC", { variant: "error" });
        }
    };

    return (
        <Dialog open={open && !!row} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle
                sx={{
                    p: "24px",
                    backgroundColor: "rgba(226, 229, 233, 0.16)",
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography color={"rgba(51, 60, 71, 1)"} fontSize={16}>{row?.keyword}</Typography>

                    <Box display="flex" alignItems="center" gap={0.8}>
                        <FlagIcon
                            isCircle={false}
                            code={countriesJson
                                .find((c) => c.name === row?.region_name)
                                ?.code?.toLowerCase()}
                            sx={{ width: 23, height: 16, position: 'relative', top: -1 }}
                        />

                        <Typography color={"rgba(51, 60, 71, 1)"} fontSize={14} whiteSpace="nowrap">
                            {row?.campaign_names}
                        </Typography>

                        <Box
                            onClick={onClose}
                            sx={{
                                ml: 1,
                                width: 22,
                                height: 22,
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "rgba(255,255,255,0.6)",
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.12)",
                                    color: "#FFF",
                                },
                            }}
                        >
                            <Iconify color={"rgba(94, 109, 123, 1)"} icon="material-symbols:close-rounded" width={16} />
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    backgroundColor: "white",
                    px: "24px",
                    pb: "24px"
                }}
            >
                <Box display="flex" gap="18px" mt={"32px"}>
                    {[
                        {
                            title: "Smart Top",
                            data: row?.scatter_plot_top_blended_presence_rate,
                            yField: "top_blended_presence_rate",
                            avg_d1: row?.top_blended_presence_rate_d1,
                            definition:
                                "How often your domain appears in the top section, whether paid or organic.",
                        },
                        {
                            title: "Smart First",
                            data: row?.scatter_plot_abs_top_blended_presence_rate,
                            yField: "abs_top_blended_presence_rate",
                            definition:
                                "How often your domain appears in the first position in the top section.",
                            avg_d1: row?.abs_top_blended_presence_rate_d1
                        },
                        {
                            title: "Paid Top",
                            data: row?.scatter_plot_top_paid_presence_rate,
                            yField: "top_paid_presence_rate",
                            definition:
                                "How often your domain appears in the paid top ads section.",
                            avg_d1: row?.top_paid_presence_rate_d1
                        },
                        {
                            title: "Paid First",
                            data: row?.scatter_plot_abs_top_paid_presence_rate,
                            yField: "abs_top_paid_presence_rate",
                            definition:
                                "How often your domain appears in the first paid position.",
                            avg_d1: row?.abs_top_paid_presence_rate_d1
                        },
                    ].map((chart, i) => {
                        const xValues = (chart.data || [])
                            .map((d: any) => Number(d.max_cpc))
                            .filter((v: number) => !isNaN(v));

                        const minX = xValues.length ? Math.min(...xValues) : undefined;
                        const maxX = xValues.length ? Math.max(...xValues) : undefined;

                        return (
                            <Box
                                key={i}
                                sx={{
                                    flex: 1,
                                    background: "rgba(226, 229, 233, 0.1)",
                                    border: "1px solid rgba(145, 158, 171, 0.12)",
                                    borderRadius: 3,
                                    p: 2.5,
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <Box sx={{ width: "100%" }} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                                    <Box display={"flex"} flexDirection={"row"}>
                                        <Typography fontWeight={600}>{chart.title}</Typography>

                                        <CustomTooltip title={chart?.definition} placement="top">
                                            <Iconify sx={{ position: "relative", top: 1, ml: "4px", cursor: "pointer" }} icon={"material-symbols:info-outline"} />
                                        </CustomTooltip>
                                    </Box>

                                    {chart?.avg_d1
                                        ? <Box display={"flex"} flexDirection={"row"} gap={'8px'}>
                                            <Typography color={"rgba(145, 158, 171, 1)"} fontSize={'14px'}>AVG</Typography>
                                            <Typography color={"rgba(145, 158, 171, 1)"} fontSize={'14px'}>{chart?.avg_d1}%</Typography>
                                        </Box>
                                        : <></>
                                    }
                                </Box>

                                <Box height={250}>
                                    <CustomLineChart
                                        data={chart.data}
                                        yField={chart.yField}
                                        maxCpcValue={maxCpc}
                                        minX={minX}
                                        maxX={maxX}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                <Box display={"flex"} justifyContent={"flex-end"} gap={2} px={"24px"} pt={"16px"}>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="#1E88E5" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography color={"rgba(30, 136, 229, 1)"} sx={{ opacity: .7 }} position={'relative'} top={2} fontSize={12}>Date & Day</Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="#7635DC" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography color={"rgba(118, 53, 220, 1)"} sx={{ opacity: .8 }} position={'relative'} top={2} fontSize={12}>Previous day (D-1)</Typography>
                    </Box>
                </Box>
            </DialogContent>

            <Box
                sx={{
                    backgroundColor: "#f6f7ff",
                    px: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box
                    sx={{
                        borderRadius: "6px",
                        background: "rgba(111, 120, 247, 0.16)",
                        display: "inline-flex",
                        alignItems: "center",
                        py: "2px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "4px",
                            p: .5,
                            background: "rgba(111, 120, 247, 0.16)"
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 16" fill="none">
                            <path d="M15.8335 13.4346L15.0001 14.3463C14.558 14.8297 13.9586 15.1013 13.3336 15.1013C12.7086 15.1013 12.1092 14.8297 11.6671 14.3463C11.2245 13.8639 10.6251 13.593 10.0003 13.593C9.37548 13.593 8.77611 13.8639 8.33347 14.3463M0.833496 15.1013H2.22895C2.6366 15.1013 2.84043 15.1013 3.03224 15.0552C3.2023 15.0144 3.36487 14.947 3.51399 14.8557C3.68219 14.7526 3.82631 14.6085 4.11457 14.3202L14.5835 3.85126C15.2739 3.16091 15.2739 2.04162 14.5835 1.35126C13.8932 0.660907 12.7739 0.660908 12.0835 1.35126L1.61455 11.8202C1.32629 12.1085 1.18217 12.2526 1.0791 12.4208C0.987715 12.5699 0.920374 12.7325 0.879546 12.9025C0.833496 13.0944 0.833496 13.2982 0.833496 13.7058V15.1013Z" stroke="#3039B0" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </Box>
                    <Typography
                        color="rgba(48, 57, 176, 1)"
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            px: '8px',
                            lineHeight: "22px",
                            m: 0,
                        }}
                    >
                        KO Bidding Manual
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center">
                    <Box
                        sx={{
                            width: "1px",
                            height: "80px",
                            backgroundColor: "rgba(255,255,255,0.10)",
                            mx: 3,
                        }}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            width: "140px",
                            height: "32px",
                            borderRadius: "8px",
                        }}
                    >
                        <TextField
                            label="Max CPC"
                            type="number"
                            variant="outlined"
                            size="small"
                            value={maxCpc}
                            onChange={(e) => {
                                const val = e.target.value === "" ? "" : Number(e.target.value);
                                if (val === "" || val >= 0) setMaxCpc(val);
                            }}
                            sx={{
                                width: 110,
                                height: 20,
                                "& .MuiInputBase-root.MuiOutlinedInput-root": {
                                    borderRadius: 0,
                                    borderTopLeftRadius: "8px",
                                    borderBottomLeftRadius: "8px",
                                    borderRight: "none",
                                },

                                "& .MuiOutlinedInput-input": {
                                    height: 15,
                                    textAlign: "right",
                                },
                            }}
                            InputProps={{
                                inputProps: {
                                    step: 0.01,
                                    min: 0,
                                },
                            }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                width: "40px",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "rgba(111, 120, 247, 0.04)",
                                borderLeft: "1px solid rgba(255,255,255,0.12)",
                                borderTopRightRadius: "8px",
                                borderBottomRightRadius: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: "rgba(177, 185, 196, 0.90)",
                                }}
                            >
                                €
                            </Typography>
                        </Box>
                    </Box>

                    <Button
                        onClick={handleApplyChanges}
                        sx={{
                            ml: 1.5,
                            background: "rgba(255, 255, 255, 1)",
                            color: "rgba(51, 60, 71, 1)",
                            fontWeight: 600,
                            textTransform: "none",
                            "&:hover": {
                                background: "rgba(255, 255, 255, .5)",
                            },
                        }}
                    >
                        Apply Max CPC
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
